import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
} from 'lexical';
import { lazy, ReactNode, Suspense } from 'react';

// Lazy load to break circular dependency with transformers.ts
const Estate360MermaidRenderer = lazy(() =>
  import('./Estate360MermaidRenderer').then((m) => ({
    default: m.Estate360MermaidRenderer,
  }))
);

// LEGACY (Mermaid): MermaidNode remains for persisted legacy custom pages/slides
// that serialized Mermaid code into Lexical/Markdown. New diagram experiences
// should use presentation diagram pages (PresentationPageKind.DiagramPage)
// instead. Do not extend this node for new diagram-page work.
// Tracked by T2-10286; revisit ~2027-06-01 to assess removal.
export class MermaidNode extends DecoratorNode<ReactNode> {
  __code: string;
  __svg?: string;

  static override getType(): string {
    return 'mermaid';
  }

  static override clone(node: MermaidNode): MermaidNode {
    const cloned = new MermaidNode(node.__code, node.__key);
    cloned.__svg = node.__svg;
    return cloned;
  }

  constructor(code: string, __key?: string) {
    super(__key);
    this.__code = code;
  }

  override createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'mermaid-block';
    div.setAttribute('data-testid', 'MermaidNode');
    div.contentEditable = 'false';
    return div;
  }

  override updateDOM(): false {
    return false;
  }

  override exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('div');
    element.className = 'mermaid-block';
    element.setAttribute('data-testid', 'MermaidNode');
    element.setAttribute('data-lexical-mermaid', 'true');
    element.setAttribute('data-mermaid-code', this.__code);
    if (this.__svg) {
      element.innerHTML = this.__svg;
    }
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mermaid')) {
          return null;
        }

        return {
          conversion: $convertMermaidElement,
          priority: 2,
        };
      },
    };
  }

  override exportJSON(): {
    code: string;
    type: string;
    version: number;
  } {
    return {
      code: this.__code,
      type: MermaidNode.getType(),
      version: 1,
    };
  }

  static override importJSON(serializedNode: {
    code: string;
    type: string;
    version: number;
  }): MermaidNode {
    const node = $createMermaidNode(serializedNode.code);
    return node;
  }

  override decorate(): ReactNode {
    return (
      <Suspense fallback={null}>
        <Estate360MermaidRenderer nodeKey={this.getKey()} code={this.__code} />
      </Suspense>
    );
  }

  getCode(): string {
    return this.__code;
  }

  setCode(code: string): void {
    const writable = this.getWritable();
    writable.__code = code;
  }
}

export function $createMermaidNode(code: string): MermaidNode {
  return new MermaidNode(code);
}

export function $isMermaidNode(
  node: LexicalNode | null | undefined
): node is MermaidNode {
  return node instanceof MermaidNode;
}

function $convertMermaidElement(
  element: HTMLElement
): DOMConversionOutput | null {
  const mermaidCode = element.getAttribute('data-mermaid-code');
  if (!mermaidCode) {
    return null;
  }

  return {
    node: $createMermaidNode(mermaidCode),
  };
}
